import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import moment from 'moment';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor, KeyValuePipe } from '@angular/common';
import { Subscription } from 'rxjs';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { TranslationService } from '../../../shared/services/translation.service';
import { WorkerService } from '../../../shared/services/worker.service';
import { ExceptionReportToDisplay, OrderComment } from '../../models/exception-report';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { Order } from '../../models/order';
import { OrderService } from '../../services/order.service';

@Component({
	selector: 'copo-exception-report',
	templateUrl: './exception-report.component.html',
	styleUrl: './exception-report.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	animations: pageTransitions,

	imports: [NgIf, MatButtonModule, MatIconModule, NgFor, TranslateModule, LoadingSpinnerComponent, KeyValuePipe]
})
export class ExceptionReportComponent implements OnInit, OnDestroy {
	exceptionReports: ExceptionReportToDisplay[] = [];
	exceptionReportMap = new Map<string, ExceptionReportToDisplay[]>();
	exportInProgress = false;
	isLoading = true;
	formatedDataToExport: ((string | number | undefined)[] | undefined)[] = [];
	orders: Order[] | undefined;
	orderFields: (keyof Order)[] = [];
	datacenterOrdersSubscription?: Subscription;
	constructor(
		private readonly orderService: OrderService,
		readonly translationService: TranslationService,
		readonly workerService: WorkerService,
	) {}

	ngOnInit() {
		this.getExceptionReport();
	}

	ngOnDestroy() {
		if (this.datacenterOrdersSubscription) {
			this.datacenterOrdersSubscription.unsubscribe();
		}
	}

	getExceptionReport() {
		const orderFields: (keyof Order)[] = [
			'cddWeek',
			'steelgrade',
			'steelgradeDescription',
			'mill',
			'millDescription',
			'customerOrderNumber',
			'customerOrderItemNumber',
			'thickness',
			'width',
			'length',
			'grossQtyOrdered',
			'grossQtyInProduction',
			'grossQtyAvailableReadyToShip',
			'grossQtyAvailableInTransit',
			'grossQtyDelivered',
			'supplierOrderNumber',
			'supplierOrderItemNumber'

		];
		const formattedWeek = moment().format('GGGG[W]WW');
		const formattedPrevWeek = moment().subtract(1, 'week').format('GGGG[W]WW');
		const formattedNextWeek = moment().add(1, 'week').format('GGGG[W]WW');
		const filter = `yearweek:${ formattedWeek } OR yearweek:${ formattedPrevWeek }
				OR yearweek:${ formattedNextWeek }`;
		this.datacenterOrdersSubscription = this.orderService.getDatacenterOrders(
			orderFields,
			filter,
			0,
			1,
			undefined,
			false
		).subscribe({
			next: res => {
				this.isLoading = false;
				this.orders = res.results;
				this.constructExceptionReport(this.orders);
			},
			error: (err: unknown) => {
				console.error('error requesting exception report fields', err);
				this.isLoading = false;
			}
		});
	}

	constructExceptionReport(orders?: Order[]) {
		if (!orders) {
			return;
		}
		for (const order of orders) {
			if (!order.comments || !order.answers) {
				return;
			}
			for (const commentCustomer of order.comments) {
				const exceptionReportToDisplay = this.buildExceptionReport(commentCustomer, order);
				exceptionReportToDisplay.commentFromCustomer = commentCustomer.comment;
				this.exceptionReports.push(exceptionReportToDisplay);
			}
			for (const commentCuse of order.answers) {
				const alreadyExist = this.exceptionReports
					.find(ex => ex.yearWeek === commentCuse.yearWeek
						&& ex.docNo === `${ order.supplierOrderNumber }/${ order.supplierOrderItemNumber }`);
				if (alreadyExist) {
					alreadyExist.commentFromCuse = commentCuse.comment;
				} else {
					const exceptionReportToDisplay = this.buildExceptionReport(commentCuse, order);
					exceptionReportToDisplay.commentFromCuse = commentCuse.comment;
					this.exceptionReports.push(exceptionReportToDisplay);
				}
			}
		}
		const exceptionReportsToDisplay: ExceptionReportToDisplay[] = this.exceptionReports.length > 5
			? this.exceptionReports.slice(0, 5)
			: this.exceptionReports;
		for (const exceptionReport of exceptionReportsToDisplay) {
			if (this.exceptionReportMap.has(exceptionReport.docNo)) {
				this.exceptionReportMap.get(exceptionReport.docNo)?.push(exceptionReport);
			} else {
				const exceptionreports: ExceptionReportToDisplay[] = [];
				exceptionreports.push(exceptionReport);
				this.exceptionReportMap.set(exceptionReport.docNo, exceptionreports);
			}
		}
	}

	buildExceptionReport(orderComment: OrderComment, order: Order) {
		const exceptionReportToDisplay: ExceptionReportToDisplay = {
			yearWeek: orderComment.yearWeek,
			docNo: `${ order.supplierOrderNumber }/${ order.supplierOrderItemNumber }`,
			customerOrderNumber: order.customerOrderNumber,
			customerOrderItemNumber: order.customerOrderItemNumber,
			steelGrade: order.steelgradeDescription,
			mill: order.millDescription,
			thickness: order.thickness,
			width: order.width,
			length: order.length,
			cdd: order.cddWeek,
			qtyOrdered: order.grossQtyOrdered,
			qtyDelivered: order.grossQtyDelivered,
			qtyWip: order.grossQtyInProduction,
			qtyInTransit: order.grossQtyAvailableInTransit,
			qtyReadyToShip: order.grossQtyAvailableReadyToShip
		};
		return exceptionReportToDisplay;
	}

	onBtExport() {
		this.exportInProgress = true;
		const columnTypes: (string[] | undefined) = [
			this.translationService.getTR('filter.supplierOrderNumber'),
			this.translationService.getTR('eoffer.dialog.week'),
			this.translationService.getTR('exception-report.comment-by-customer'),
			this.translationService.getTR('exception-report.comment-by-am'),
			this.translationService.getTR('order.customer.order.number'),
			this.translationService.getTR('batch-card.steelgrade'),
			this.translationService.getTR('batch.card.dimensions'),
			this.translationService.getTR('datacenter-filter.cdd-date'),
			this.translationService.getTR('filter.mill'),
			this.translationService.getTR('commercial-documents.order-quantity'),
			this.translationService.getTR('schedule-business-order.wip'),
			this.translationService.getTR('filter.readyToShip'),
			this.translationService.getTR('filter.inTransit'),
			this.translationService.getTR('filter.delivered'),
		];
		if (!this.orders) {
			return;
		}
		this.formatedDataToExport = this.formatExportedData(columnTypes, this.exceptionReports);
		const worker = this.workerService.createWorkerExcel();
		const fileName = `Exception Report-${ moment().format('YYYY MM DD') }.xlsx`;
		worker.addEventListener('message', ({ data }: { data: string }) => {
			this.exportInProgress = false;
			const link = document.createElement('a');
			link.download = fileName;
			link.href = data;
			link.append(document.createTextNode(fileName));
			document.body.append(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(data);
			worker.terminate();
		});
		const sheets = [
			{
				fileName,
				rows: this.formatedDataToExport,
				workSheetName: 'Exception Report',
				columnTypes
			}
		];
		worker.postMessage({
			sheets
		});
	}

	buildYearWeek(yearWeek: string) {
		const year = yearWeek.slice(0, yearWeek.indexOf('W'));
		const week = yearWeek.slice(yearWeek.indexOf('W') + 1);

		return `Week ${ week } ${ year }`;
	}

	formatExportedData(columnTypes: string[], exceptionReports: ExceptionReportToDisplay[]) {
		return [
			columnTypes.map(columnDef => columnDef),
			...exceptionReports.map(exceptionReport => columnTypes.map(columnDef => {
				switch (columnDef) {
				case this.translationService.getTR('filter.supplierOrderNumber'):
					return exceptionReport.docNo;
				case this.translationService.getTR('eoffer.dialog.week'):
					return this.buildYearWeek(exceptionReport.yearWeek);
				case this.translationService.getTR('exception-report.comment-by-customer'):
					return exceptionReport.commentFromCustomer;
				case this.translationService.getTR('exception-report.comment-by-am'):
					return exceptionReport.commentFromCuse;
				case this.translationService.getTR('order.customer.order.number'):
					return `${ exceptionReport.customerOrderNumber }/${ exceptionReport.customerOrderItemNumber }`;
				case this.translationService.getTR('batch-card.steelgrade'):
					return exceptionReport.steelGrade;
				case this.translationService.getTR('batch.card.dimensions'): {
					const invalidValue = '';
					if (!exceptionReport.thickness || !exceptionReport.width) {
						return invalidValue;
					}
					const lengthPart = exceptionReport.length ? ` ×\u00A0${ exceptionReport.length }` : '';
					return `${ exceptionReport.thickness } ×\u00A0${ exceptionReport.width }${ lengthPart }`;
				}
				case this.translationService.getTR('datacenter-filter.cdd-date'): {
					const match = exceptionReport.cdd?.match(/^(\d{4})-W(\d{2})$/);
					if (match) {
						const [, year, week] = match;
						return `W${ week }/${ year }`;
					}
					return '';
				}
				case this.translationService.getTR('filter.mill'):
					return exceptionReport.mill;
				case this.translationService.getTR('commercial-documents.order-quantity'):
					return exceptionReport.qtyOrdered;
				case this.translationService.getTR('schedule-business-order.wip'):
					return exceptionReport.qtyWip;
				case this.translationService.getTR('filter.readyToShip'):
					return exceptionReport.qtyReadyToShip;
				case this.translationService.getTR('filter.inTransit'):
					return exceptionReport.qtyInTransit;
				case this.translationService.getTR('filter.delivered'):
					return exceptionReport.qtyDelivered;
				default:
					return '';
				}
			})),
		];
	}
}
